import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import {
  Plus, Loader2, ShieldCheck, X, UserCheck, UserX,
  BarChart3, Clock, Briefcase, Receipt, Activity,
  Search, Filter, ChevronDown, ChevronUp
} from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C]";
const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtF = (s) => { try { return new Date(s).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return s || '—'; } };

const ROLES = ['Socio Director', 'Socio Sub Director', 'Socio Mayoritario', 'Socio Minoritario', 'Socio Junior', 'Pasante', 'Coordinacion General'];
const MODULOS = ['dashboard', 'expedientes', 'clientes', 'agenda', 'facturacion', 'reportes', 'vault', 'admin'];

/* ─── Modal crear usuario ─────────────────────────────────── */
function ModalCrear({ onClose, onCreated }) {
  const [f, setF] = useState({ nombre: '', apellido: '', email: '', password_plana: '', rol: 'Socio Junior' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSaving(true);
    try { await api.post('/api/admin/registrar-usuario', f); onCreated(); }
    catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Revise los datos (contraseña: 10+ chars, mayús, minús, número, símbolo)');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Nuevo integrante</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Nombre *" value={f.nombre} onChange={set('nombre')} required />
            <input className={IC} placeholder="Apellido" value={f.apellido} onChange={set('apellido')} />
          </div>
          <input className={IC} type="email" placeholder="Email *" value={f.email} onChange={set('email')} required />
          <input className={IC} type="password" placeholder="Contraseña inicial *" value={f.password_plana} onChange={set('password_plana')} required />
          <select className={IC} value={f.rol} onChange={set('rol')}>
            {ROLES.map((r) => <option key={r} value={r} className="bg-[#141414]">{r}</option>)}
          </select>
          <p className="text-[10px] text-[#666]">El usuario deberá cambiar la contraseña en su primer ingreso.</p>
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear integrante'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Modal permisos ──────────────────────────────────────── */
function ModalPermisos({ usuario, onClose, onSaved }) {
  const [rol, setRol] = useState(usuario.rol || 'Socio Junior');
  const [permisos, setPermisos] = useState(usuario.permisos || {});
  const [saving, setSaving] = useState(false);
  const toggle = (m) => setPermisos((p) => ({ ...p, [m]: !p[m] }));

  const guardar = async () => {
    setSaving(true);
    try { await api.put(`/api/admin/usuarios/${usuario.id}/rol-permisos`, { rol, permisos }); onSaved(); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Rol y permisos</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#999] mb-3">{usuario.nombre} {usuario.apellido} · {usuario.email}</p>
        <select className={IC + ' mb-4'} value={rol} onChange={(e) => setRol(e.target.value)}>
          <option value="admin" className="bg-[#141414]">Admin</option>
          {ROLES.map((r) => <option key={r} value={r} className="bg-[#141414]">{r}</option>)}
        </select>
        <div className="space-y-2 mb-5">
          {MODULOS.map((m) => (
            <label key={m} className="flex items-center justify-between py-1.5 cursor-pointer">
              <span className="text-sm capitalize">{m}</span>
              <button type="button" onClick={() => toggle(m)}
                className={`w-10 h-5 rounded-full transition-all relative ${permisos[m] ? 'bg-[#C9A44C]' : 'bg-white/10'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${permisos[m] ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </label>
          ))}
        </div>
        <button onClick={guardar} disabled={saving}
          className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}

const PLAN_LABELS = {
  independiente: 'Independiente',
  estudio_5: 'Estudio hasta 5',
  estudio_10: 'Estudio hasta 10',
  custom: 'A medida',
};

/* ─── Página Administración ───────────────────────────────── */
export default function Administracion() {
  const [tab, setTab] = useState('usuarios');
  const [usuarios, setUsuarios] = useState([]);
  const [metricas, setMetricas] = useState(null);
  const [auditoria, setAuditoria] = useState([]);
  const [config, setConfig] = useState(null);
  const [uso, setUso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCrear, setShowCrear] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroAud, setFiltroAud] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  const cargarUsuarios = async () => {
    const [r, u] = await Promise.all([
      api.get('/api/admin/usuarios'),
      api.get('/api/admin/plan/uso'),
    ]);
    setUsuarios(r.data.data || []);
    setUso(u.data);
  };
  const cargarMetricas = async () => {
    const r = await api.get('/api/admin/metricas');
    setMetricas(r.data);
  };
  const cargarAuditoria = async (filtro = '') => {
    const params = filtro ? `?entidad=${encodeURIComponent(filtro)}` : '';
    const r = await api.get(`/api/admin/auditoria${params}`);
    setAuditoria(r.data);
  };
  const cargarConfig = async () => {
    const r = await api.get('/api/admin/config');
    setConfig(r.data);
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([cargarUsuarios(), cargarMetricas(), cargarAuditoria(), cargarConfig()])
      .finally(() => setLoading(false));
  }, []);

  const toggleActivo = async (id) => {
    await api.put(`/api/admin/usuarios/${id}/toggle-activo`);
    cargarUsuarios();
  };

  const guardarConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/api/admin/config', {
        nombre_estudio: config.nombre_estudio,
        cuit: config.cuit,
        direccion: config.direccion,
        iva_default: parseFloat(config.iva_default) || 21,
        smvm_base: parseFloat(config.smvm_base) || 352400,
        valor_jus: parseFloat(config.valor_jus) || 25400,
      });
    } finally { setSavingConfig(false); }
  };

  const TABS = [
    { id: 'usuarios', label: 'Integrantes', icon: ShieldCheck },
    { id: 'metricas', label: 'Métricas KPI', icon: BarChart3 },
    { id: 'auditoria', label: 'Auditoría', icon: Activity },
    { id: 'config', label: 'Configuración', icon: Briefcase },
  ];

  return (
    <DashboardLayout title="Administración">
      {/* Tabs */}
      <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs whitespace-nowrap transition-all ${
              tab === id
                ? 'bg-[#C9A44C] text-[#070707] font-semibold'
                : 'bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] text-[#888] hover:text-[#F8F6F0]'
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
      ) : (
        <>
          {/* ── Usuarios ────────────────────────────────────── */}
          {tab === 'usuarios' && (
            <>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <div>
                  <p className="text-sm text-[#888]">Gestión de roles y accesos por jerarquía</p>
                  {uso && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <span className="text-[#888]">
                          Plan <span className="text-[#C9A44C]">{PLAN_LABELS[uso.plan] || uso.plan}</span>
                        </span>
                        <span className="text-[#666]">·</span>
                        <span className={uso.puede_agregar ? 'text-green-400' : 'text-red-400'}>
                          {uso.usuarios_activos} / {uso.limite_usuarios === 9999 ? '∞' : uso.limite_usuarios} usuarios
                        </span>
                        {!uso.puede_agregar && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/25">
                            Límite alcanzado
                          </span>
                        )}
                        {uso.puede_agregar && uso.slots_disponibles <= 2 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C9A44C]/15 text-[#C9A44C] border border-[#C9A44C]/25">
                            {uso.slots_disponibles} slot{uso.slots_disponibles !== 1 ? 's' : ''} disponible{uso.slots_disponibles !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {uso.limite_usuarios < 9999 && (
                        <div className="w-48 h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${Math.min(100, (uso.usuarios_activos / uso.limite_usuarios) * 100)}%`,
                              background: uso.puede_agregar
                                ? 'linear-gradient(90deg, #C9A44C, #E0C878)'
                                : 'linear-gradient(90deg, #EF4444, #F87171)',
                            }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <button
                    onClick={() => uso?.puede_agregar ? setShowCrear(true) : null}
                    disabled={uso && !uso.puede_agregar}
                    title={uso && !uso.puede_agregar ? `Límite de tu plan alcanzado (${uso.usuarios_activos}/${uso.limite_usuarios}). Actualizá el plan para agregar más.` : ''}
                    className="text-[#070707] font-semibold px-5 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                    <Plus className="w-4 h-4" /> Nuevo integrante
                  </button>
                  {uso && !uso.puede_agregar && (
                    <p className="text-[10px] text-red-400 text-right max-w-[220px]">
                      Límite del plan alcanzado. <a href="/suscripcion" className="underline">Actualizá tu plan</a> para agregar más.
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2.5">
                {usuarios.map((u) => (
                  <div key={u.id} className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-[#C9A44C]/12 border border-[#C9A44C]/25 flex items-center justify-center text-[#C9A44C] flex-shrink-0">
                        {(u.nombre || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{u.nombre} {u.apellido}</div>
                        <div className="text-[11px] text-[#888] truncate">{u.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-[10px] uppercase tracking-wide px-2.5 py-1 rounded-full border border-[#C9A44C]/20 text-[#C9A44C] bg-[#C9A44C]/10 hidden sm:inline">{u.rol}</span>
                      <button onClick={() => toggleActivo(u.id)} title={u.activo ? 'Desactivar' : 'Activar'}
                        className={u.activo ? 'text-green-400 hover:text-green-300' : 'text-[#666] hover:text-[#999]'}>
                        {u.activo ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setEditando(u)} className="text-[#C9A44C] hover:text-[#E0C878]">
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Métricas KPI ────────────────────────────────── */}
          {tab === 'metricas' && metricas && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Receipt, label: 'Facturación mensual', val: metricas.facturacion_mensual },
                  { icon: Briefcase, label: 'Casos totales', val: metricas.casos_totales },
                  { icon: Receipt, label: 'Total bruto', val: fmt(metricas.total_bruto_estudio) },
                  { icon: Clock, label: 'Saldo pendiente', val: fmt(metricas.saldo_pendiente_estudio), col: 'text-red-300' },
                ].map(({ icon: Icon, label, val, col }) => (
                  <div key={label} className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
                    <Icon className="w-5 h-5 text-[#C9A44C] mb-2" />
                    <div className={`text-xl font-light mb-0.5 ${col || ''}`} style={{ fontFamily: 'Playfair Display, serif' }}>{val}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#888]">{label}</div>
                  </div>
                ))}
              </div>

              {/* Barra de cobranza */}
              <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
                <div className="flex justify-between mb-2">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#C9A44C]">Tasa de cobranza del estudio</span>
                  <span className="text-sm text-[#C9A44C]">
                    {metricas.total_bruto_estudio > 0
                      ? Math.round((metricas.total_cobrado_estudio / metricas.total_bruto_estudio) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-white/5">
                  <div className="h-full rounded-full" style={{
                    width: `${metricas.total_bruto_estudio > 0 ? Math.round((metricas.total_cobrado_estudio / metricas.total_bruto_estudio) * 100) : 0}%`,
                    background: 'linear-gradient(90deg, #C9A44C, #E0C878)'
                  }} />
                </div>
                <div className="flex justify-between mt-2 text-xs text-[#888]">
                  <span>Cobrado: {fmt(metricas.total_cobrado_estudio)}</span>
                  <span>Total: {fmt(metricas.total_bruto_estudio)}</span>
                </div>
              </div>

              {/* Rendimiento por abogado */}
              {metricas.rendimiento_abogados?.nombres?.length > 0 && (
                <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#C9A44C] mb-4">Casos por abogado</h3>
                  <div className="space-y-2">
                    {metricas.rendimiento_abogados.nombres.map((nombre, i) => {
                      const casos = metricas.rendimiento_abogados.casos_asignados[i] || 0;
                      const max = Math.max(...metricas.rendimiento_abogados.casos_asignados, 1);
                      return (
                        <div key={i}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-[#ccc]">{nombre}</span>
                            <span className="text-[#C9A44C]">{casos}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-white/5">
                            <div className="h-full rounded-full" style={{ width: `${(casos / max) * 100}%`, background: 'linear-gradient(90deg, #C9A44C, #E0C878)' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Auditoría ────────────────────────────────────── */}
          {tab === 'auditoria' && (
            <>
              <div className="flex gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
                  <input className={IC + ' pl-10 text-sm'} placeholder="Filtrar por entidad (cliente, expediente…)"
                    value={filtroAud} onChange={(e) => setFiltroAud(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && cargarAuditoria(filtroAud)} />
                </div>
                <button onClick={() => cargarAuditoria(filtroAud)}
                  className="px-4 py-2 rounded-lg border border-[rgba(201,164,76,0.25)] text-[#C9A44C] hover:bg-[#C9A44C]/10 text-sm transition-all">
                  Filtrar
                </button>
              </div>
              <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
                <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_2fr_1fr] gap-4 px-4 py-2.5 border-b border-white/5 text-[10px] uppercase tracking-[0.15em] text-[#666]">
                  <span>Usuario</span><span>Acción</span><span>Entidad</span><span>Detalles</span><span>Fecha</span>
                </div>
                {auditoria.length === 0 ? (
                  <p className="text-sm text-[#555] py-8 text-center">Sin registros de auditoría</p>
                ) : (
                  <div className="divide-y divide-white/[0.04] max-h-[500px] overflow-y-auto">
                    {auditoria.map((a) => (
                      <div key={a.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_2fr_1fr] gap-2 sm:gap-4 px-4 py-3 hover:bg-white/[0.02] transition-all">
                        <span className="text-xs text-[#ccc] truncate">{a.usuario_nombre || a.usuario_email || '—'}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border w-fit ${
                          a.accion === 'crear' ? 'text-green-300 bg-green-500/10 border-green-500/20'
                          : a.accion === 'eliminar' ? 'text-red-300 bg-red-500/10 border-red-500/20'
                          : 'text-[#C9A44C] bg-[#C9A44C]/10 border-[#C9A44C]/20'
                        }`}>{a.accion}</span>
                        <span className="text-xs text-[#888]">{a.entidad}</span>
                        <span className="text-xs text-[#666] truncate">{a.detalles ? JSON.stringify(a.detalles).slice(0, 60) : '—'}</span>
                        <span className="text-[10px] text-[#666]">{fmtF(a.fecha)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Configuración del estudio ────────────────────── */}
          {tab === 'config' && config && (
            <div className="max-w-lg space-y-4">
              <p className="text-sm text-[#888] mb-4">Parámetros globales de la organización</p>
              {[
                { k: 'nombre_estudio', label: 'Nombre del estudio' },
                { k: 'cuit', label: 'CUIT' },
                { k: 'direccion', label: 'Dirección' },
                { k: 'iva_default', label: 'IVA por defecto (%)', type: 'number' },
                { k: 'smvm_base', label: 'SMVM base ($)', type: 'number' },
                { k: 'valor_jus', label: 'Valor JUS ($)', type: 'number' },
              ].map(({ k, label, type }) => (
                <div key={k}>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-[#888] block mb-1">{label}</label>
                  <input className={IC} type={type || 'text'} value={config[k] || ''}
                    onChange={(e) => setConfig((p) => ({ ...p, [k]: e.target.value }))} />
                </div>
              ))}
              <button onClick={guardarConfig} disabled={savingConfig}
                className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-lg text-[#070707] font-semibold disabled:opacity-50 mt-2"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar configuración'}
              </button>
            </div>
          )}
        </>
      )}

      {showCrear && <ModalCrear onClose={() => setShowCrear(false)} onCreated={() => { setShowCrear(false); cargarUsuarios(); }} />}
      {editando && <ModalPermisos usuario={editando} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargarUsuarios(); }} />}
    </DashboardLayout>
  );
}
